import { useState, useEffect } from "react";
import { api } from "@/App";
import { 
  Plus, Trash2, GripVertical, Save, Wrench, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const COLORS = [
  { value: "amber", label: "Jaune", class: "bg-amber-100 text-amber-700" },
  { value: "orange", label: "Orange", class: "bg-orange-100 text-orange-700" },
  { value: "blue", label: "Bleu", class: "bg-blue-100 text-blue-700" },
  { value: "purple", label: "Violet", class: "bg-purple-100 text-purple-700" },
  { value: "emerald", label: "Vert", class: "bg-emerald-100 text-emerald-700" },
  { value: "red", label: "Rouge", class: "bg-red-100 text-red-700" },
  { value: "pink", label: "Rose", class: "bg-pink-100 text-pink-700" },
  { value: "cyan", label: "Cyan", class: "bg-cyan-100 text-cyan-700" },
];

export default function WorkflowConfig() {
  const [steps, setSteps] = useState([]);
  const [specialites, setSpecialites] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [newStep, setNewStep] = useState({
    label: "",
    specialite: "carrossier",
    color: "blue"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [stepsRes, specsRes, settingsRes] = await Promise.all([
        api.get("/workflow-steps"),
        api.get("/specialites"),
        api.get("/settings")
      ]);
      setSteps(stepsRes.data);
      setSpecialites(specsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const handleAddStep = async () => {
    if (!newStep.label) {
      toast.error("Entrez un nom pour l'étape");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/workflow-steps", {
        name: newStep.label.toLowerCase().replace(/\s+/g, "_"),
        label: newStep.label,
        specialite: newStep.specialite,
        color: newStep.color
      });
      toast.success("Étape ajoutée");
      setDialogOpen(false);
      setNewStep({ label: "", specialite: "carrossier", color: "blue" });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  const handleDeleteStep = async (stepId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette étape ?")) return;
    
    try {
      await api.delete(`/workflow-steps/${stepId}`);
      toast.success("Étape supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleUpdateStep = async (stepId, field, value) => {
    try {
      await api.put(`/workflow-steps/${stepId}`, { [field]: value });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("Paramètres enregistrés");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const getColorClass = (color) => {
    const c = COLORS.find(col => col.value === color);
    return c?.class || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="workflow-config">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Configuration Workflow</h1>
        <p className="text-slate-600 mt-1">Gérez les étapes du workflow carrosserie</p>
      </div>

      {/* Workflow Steps */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Étapes du Workflow
            </CardTitle>
            <CardDescription>Ajoutez, modifiez ou supprimez des étapes</CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="mr-2 h-4 w-4" />
            Ajouter une étape
          </Button>
        </CardHeader>
        <CardContent>
          {steps.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Aucune étape configurée</p>
          ) : (
            <div className="space-y-3">
              {steps.map((step, idx) => (
                <div
                  key={step.id}
                  className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 bg-white"
                  data-testid={`workflow-step-${step.id}`}
                >
                  <div className="flex items-center gap-2 text-slate-400">
                    <GripVertical className="h-5 w-5" />
                    <span className="font-bold text-slate-600">{idx + 1}</span>
                  </div>
                  
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      value={step.label}
                      onChange={(e) => handleUpdateStep(step.id, "label", e.target.value)}
                      placeholder="Nom de l'étape"
                    />
                    
                    <Select
                      value={step.specialite}
                      onValueChange={(v) => handleUpdateStep(step.id, "specialite", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {specialites.filter(s => s.name !== "admin").map((spec) => (
                          <SelectItem key={spec.id} value={spec.name}>
                            {spec.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select
                      value={step.color}
                      onValueChange={(v) => handleUpdateStep(step.id, "color", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLORS.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded ${color.class}`} />
                              {color.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Badge className={getColorClass(step.color)}>
                    {step.label}
                  </Badge>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteStep(step.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Paramètres Planning
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Heures de travail par jour</Label>
              <Input
                type="number"
                min="1"
                max="24"
                value={settings.heures_travail_jour || 8}
                onChange={(e) => setSettings({...settings, heures_travail_jour: parseInt(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label>Capacité max véhicules/jour</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={settings.capacite_vehicules_jour || 5}
                onChange={(e) => setSettings({...settings, capacite_vehicules_jour: parseInt(e.target.value)})}
              />
            </div>
          </div>
          <Button onClick={handleSaveSettings} className="bg-red-600 hover:bg-red-700">
            <Save className="mr-2 h-4 w-4" />
            Enregistrer les paramètres
          </Button>
        </CardContent>
      </Card>

      {/* Add Step Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle étape</DialogTitle>
            <DialogDescription>Ajoutez une nouvelle étape au workflow</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom de l'étape *</Label>
              <Input
                placeholder="Ex: Géométrie, Lister pièces..."
                value={newStep.label}
                onChange={(e) => setNewStep({...newStep, label: e.target.value})}
                data-testid="input-step-label"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Spécialité assignée</Label>
              <Select
                value={newStep.specialite}
                onValueChange={(v) => setNewStep({...newStep, specialite: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {specialites.filter(s => s.name !== "admin").map((spec) => (
                    <SelectItem key={spec.id} value={spec.name}>
                      {spec.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Couleur</Label>
              <Select
                value={newStep.color}
                onValueChange={(v) => setNewStep({...newStep, color: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${color.class}`} />
                        {color.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleAddStep}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="save-step-btn"
            >
              {saving ? <div className="spinner" /> : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
